import { Typography } from 'antd';
import { formatDateWithFallback, type DateFormatStyle, type FormatDateOptions } from '../../utils/dateFormat';

const { Text } = Typography;

type DateInput = Date | string | number | null | undefined;

export interface DateDisplayProps {
  /** 表示する日付値 */
  value: DateInput;
  /** 曜日を表示するか（デフォルト: true） */
  showWeekday?: boolean;
  /** 時間を表示するか（デフォルト: false） */
  showTime?: boolean;
  /** 秒を表示するか（デフォルト: false、showTimeがtrueの場合のみ有効） */
  showSeconds?: boolean;
  /** 日付のフォーマット（デフォルト: 'slash'） */
  format?: DateFormatStyle;
  /** 値が無効な場合のフォールバック文字列（デフォルト: '-'） */
  fallback?: string;
  /** Typography.Textのtype属性 */
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  /** 強調表示するか */
  strong?: boolean;
  /** 追加のCSSクラス */
  className?: string;
  /** 追加のスタイル */
  style?: React.CSSProperties;
}

/**
 * 日付を統一フォーマットで表示する共通コンポーネント
 *
 * @example
 * // 基本: 2024/12/05 (木)
 * <DateDisplay value={date} />
 *
 * // 時間付き: 2024/12/05 (木) 14:30
 * <DateDisplay value={date} showTime />
 *
 * // 曜日なし: 2024/12/05
 * <DateDisplay value={date} showWeekday={false} />
 *
 * // 日本語形式: 2024年12月5日 (木)
 * <DateDisplay value={date} format="japanese" />
 *
 * // 秒付き: 2024/12/05 (木) 14:30:45
 * <DateDisplay value={date} showTime showSeconds />
 */
export function DateDisplay({
  value,
  showWeekday = true,
  showTime = false,
  showSeconds = false,
  format = 'slash',
  fallback = '-',
  type,
  strong,
  className,
  style,
}: DateDisplayProps): JSX.Element {
  const options: FormatDateOptions = {
    showWeekday,
    showTime,
    showSeconds,
    format,
  };

  const formatted = formatDateWithFallback(value, options, fallback);

  return (
    <Text type={type} strong={strong} className={className} style={style}>
      {formatted}
    </Text>
  );
}
